import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import copy from '../copy';

type AuthMode = 'login' | 'register' | 'password-reset' | 'success';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, register, resetPassword, isLoading } = useAuth();
  const [mode, setMode] = useState<AuthMode>('login');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estados para formularios
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [resetEmail, setResetEmail] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await login(loginData);
    if (success) {
      setSuccess(copy.auth.login.success);
      setMode('success');
    } else {
      setError(copy.auth.login.error);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (registerData.password !== registerData.confirmPassword) {
      setError(copy.auth.register.passwordMismatch);
      return;
    }
    
    const success = await register(registerData);
    if (success) {
      setSuccess(copy.auth.register.success);
      setMode('success');
    } else {
      setError(copy.auth.register.error);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const success = await resetPassword(resetEmail);
    if (success) {
      setSuccess(copy.auth.passwordReset.sent);
      setMode('success');
    } else {
      setError(copy.auth.passwordReset.error);
    }
  };

  const goToCampus = () => {
    window.open(copy.auth.login.moodleUrl, '_blank');
  };

  const renderLoginForm = () => (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{copy.auth.login.title}</h2>
      <p className="text-gray-600 mb-6">{copy.auth.login.subtitle}</p>
      
      <form onSubmit={handleLogin} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.login.email}
          </label>
          <input
            type="email"
            value={loginData.email}
            onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.login.password}
          </label>
          <input
            type="password"
            value={loginData.password}
            onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Cargando...' : copy.auth.login.button}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <button
          onClick={() => setMode('password-reset')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.login.forgot}
        </button>
      </div>
      
      <div className="mt-4 text-center">
        <span className="text-gray-600 text-sm">{copy.auth.register.hasAccount} </span>
        <button
          onClick={() => setMode('register')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.register.login}
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{copy.auth.register.title}</h2>
      <p className="text-gray-600 mb-6">{copy.auth.register.subtitle}</p>
      
      <form onSubmit={handleRegister} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {copy.auth.register.firstName}
            </label>
            <input
              type="text"
              value={registerData.firstName}
              onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {copy.auth.register.lastName}
            </label>
            <input
              type="text"
              value={registerData.lastName}
              onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.email}
          </label>
          <input
            type="email"
            value={registerData.email}
            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.phone}
          </label>
          <input
            type="tel"
            value={registerData.phone}
            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="+52 55 1234 5678"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.password}
          </label>
          <input
            type="password"
            value={registerData.password}
            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.register.confirmPassword}
          </label>
          <input
            type="password"
            value={registerData.confirmPassword}
            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Cargando...' : copy.auth.register.button}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <span className="text-gray-600 text-sm">{copy.auth.login.noAccount} </span>
        <button
          onClick={() => setMode('login')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.login.register}
        </button>
      </div>
    </div>
  );

  const renderPasswordResetForm = () => (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{copy.auth.passwordReset.title}</h2>
      <p className="text-gray-600 mb-6">{copy.auth.passwordReset.subtitle}</p>
      
      <form onSubmit={handlePasswordReset} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {copy.auth.passwordReset.email}
          </label>
          <input
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        
        {error && <p className="text-red-600 text-sm">{error}</p>}
        
        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? 'Cargando...' : copy.auth.passwordReset.button}
        </button>
      </form>
      
      <div className="mt-4 text-center">
        <button
          onClick={() => setMode('login')}
          className="text-blue-600 hover:text-blue-800 text-sm"
        >
          {copy.auth.passwordReset.back}
        </button>
      </div>
    </div>
  );

  const renderSuccessView = () => (
    <div className="max-w-md mx-auto bg-white p-8 rounded-lg shadow-md text-center">
      <div className="text-green-600 text-6xl mb-4">✓</div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{success}</h2>
      
      <div className="mt-6 space-y-3">
        <button
          onClick={goToCampus}
          className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
        >
          {copy.auth.login.goToCampus}
        </button>
        
        <button
          onClick={() => navigate('/')}
          className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
        >
          Volver al inicio
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {mode === 'login' && renderLoginForm()}
      {mode === 'register' && renderRegisterForm()}
      {mode === 'password-reset' && renderPasswordResetForm()}
      {mode === 'success' && renderSuccessView()}
    </div>
  );
};

export default Login; 